// markdown-tokenizer.js — the PURE parsing layer for the shared legal-page
// renderer (MarkdownBody). Extracted from App.jsx (2026-06-15) so the parser
// can be unit-tested in node-vitest without importing the heavy browser
// module or any JSX.
//
// Split of responsibility:
//   • tokenizeMarkdown(text) → token[]   — pure, no React, no JSX (this file).
//   • MarkdownBody (markdown-body.jsx)    — renders tokens to React elements.
//
// SECURITY: this layer never produces HTML. It emits plain token objects;
// the renderer turns them into React elements (auto-escaped). Link URLs are
// passed through safeMdLink() so the renderer only ever receives a vetted href.
//
// PHASE 1 (extraction, behaviour-identical to the old App.jsx MarkdownBody):
//   token types: 'br' | 'h2' | 'h3' | 'p'
//   Every non-blank, non-heading line → a 'p' token (literal), exactly as before.
// PHASE 2 adds 'hr' | 'ul' | 'table' additively (see below).

// Whitelist link protocols/shapes; anything else collapses to '#'.
// Moved verbatim from App.jsx so both MarkdownBody and mdToHtml share one copy.
export function safeMdLink(url) {
  if (!url) return "#";
  const u = String(url).trim();
  if (u.startsWith("http://") || u.startsWith("https://")
      || u.startsWith("/") || u.startsWith("#")
      || u.startsWith("mailto:") || u.startsWith("tel:")) {
    return u;
  }
  return "#";
}

// ── Phase-2 line classifiers (additive) ─────────────────────────────────────
// Bullet: line begins with "- " or "* " (marker + whitespace). "**bold**"
// starts with "*" + "*" (not whitespace) so it is NOT a bullet.
const isBullet = (l) => /^[-*]\s+/.test(l);
// A table BODY/HEADER row begins with a pipe.
const isTableRow = (l) => l.startsWith('|');
// A table SEPARATOR row: pipes + dashes (optional alignment colons), ≥2 cols,
// e.g. |---|---| or | :--- | ---: |.
const isTableSep = (l) => /^\|?\s*:?-{1,}:?\s*(\|\s*:?-{1,}:?\s*)+\|?$/.test(l);
// Horizontal rule — a line that is exactly ---, *** or ___.
const isHr = (l) => l === '---' || l === '***' || l === '___';
// Split a "| a | b |" row into trimmed cells (drops the outer pipes).
const splitRow = (l) => l.replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim());

// Pure markdown → token array. No JSX, no DOM, no HTML.
// Token types: 'br' | 'h2' | 'h3' | 'p' | 'hr' | 'ul' | 'table'.
// Additive design: the hr/ul/table branches fire ONLY on their markdown
// markers; any line that is not a heading/blank/hr/table/bullet still becomes
// a 'p' token exactly as in Phase 1 — so pure-prose docs are byte-identical.
export function tokenizeMarkdown(text) {
  if (!text) return [];
  const lines = text.split('\n');
  const tokens = [];
  let i = 0;
  while (i < lines.length) {
    const trimmed = lines[i].trim();
    if (!trimmed) { tokens.push({ type: 'br' }); i++; continue; }
    if (trimmed.startsWith('## ')) { tokens.push({ type: 'h2', text: trimmed.slice(3) }); i++; continue; }
    if (trimmed.startsWith('### ')) { tokens.push({ type: 'h3', text: trimmed.slice(4) }); i++; continue; }
    if (isHr(trimmed)) { tokens.push({ type: 'hr' }); i++; continue; }

    // Table — a pipe row IMMEDIATELY followed by a separator row. Defensive:
    // a stray "| …" line without a following separator is NOT a table → it
    // falls through to a literal <p> (no mis-parse of incidental pipes).
    if (isTableRow(trimmed) && i + 1 < lines.length && isTableSep(lines[i + 1].trim())) {
      const header = splitRow(trimmed);
      i += 2; // consume header + separator
      const rows = [];
      while (i < lines.length && isTableRow(lines[i].trim())) {
        rows.push(splitRow(lines[i].trim()));
        i++;
      }
      tokens.push({ type: 'table', header, rows });
      continue;
    }

    // Bullet list — a run of consecutive "- "/"* " lines becomes one <ul>.
    if (isBullet(trimmed)) {
      const items = [];
      while (i < lines.length && isBullet(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^[-*]\s+/, ''));
        i++;
      }
      tokens.push({ type: 'ul', items });
      continue;
    }

    tokens.push({ type: 'p', text: trimmed });
    i++;
  }
  return tokens;
}
